import type { DateRangePreset } from '@/components/filters/date-range-presets';
import {
  FilterButton,
  FilterButtonAction,
  FilterButtonActiveOptions,
  FilterButtonLabel,
  FilterButtonMenu,
  FilterButtonMenuHeader,
  type PopoverPlacement,
} from '@/components/filters/filter-button';
import { DatePicker } from '@/components/ui/date-picker/DatePicker';
import { Box, Button, HStack, Popover, Portal, Separator, VStack } from '@chakra-ui/react';
import type { DateValue } from '@internationalized/date';
import { parseDate } from '@internationalized/date';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Helper function to format a DateValue to YYYY-MM-DD string
const formatDateValue = (date: DateValue | null): string => {
  if (!date) return '';
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
};

// Helper function to parse YYYY-MM-DD string to DateValue
const parseDateString = (dateString: string | null): DateValue | null => {
  if (!dateString) return null;
  try {
    return parseDate(dateString);
  } catch (e) {
    console.error('Error parsing date string:', e);
    return null;
  }
};

// Helper to prevent closing when interacting with DatePicker dialog
const handleDateFilterInteractOutside = (event: Event) => {
  if ((event.target as Element)?.closest('[role="dialog"]')) {
    event.preventDefault();
  }
};

interface DateRangeFilterProps {
  paramPrefix?: string; // Optional prefix for URL parameters
  presets?: DateRangePreset[]; // Optional preset configurations
  showPresets?: boolean; // Show preset buttons (default: false)
  positioning?: { placement?: PopoverPlacement }; // Optional popover positioning
  defaultPreset?: string; // Default preset value to apply on mount (e.g., '30d', '7d')
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  paramPrefix = '',
  presets = [],
  showPresets = false,
  positioning,
  defaultPreset,
}) => {
  const { t } = useTranslation();

  // URL state management with nuqs
  const startDateParam = paramPrefix ? `${paramPrefix}StartDate` : 'startDate';
  const endDateParam = paramPrefix ? `${paramPrefix}EndDate` : 'endDate';

  const [urlStartDate, setUrlStartDate] = useQueryState(startDateParam, parseAsString);
  const [urlEndDate, setUrlEndDate] = useQueryState(endDateParam, parseAsString);

  // Set default preset on mount if no dates are in URL
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current) return;
    if (!urlStartDate && !urlEndDate && showPresets && presets.length > 0 && defaultPreset) {
      // Find the preset matching the defaultPreset value
      const preset = presets.find((p) => p.value === defaultPreset);
      if (preset) {
        const { start, end } = preset.getDates();
        setUrlStartDate(formatDateValue(start));
        setUrlEndDate(formatDateValue(end));
        hasInitialized.current = true;
      }
    }
  }, [
    urlStartDate,
    urlEndDate,
    showPresets,
    presets,
    defaultPreset,
    setUrlStartDate,
    setUrlEndDate,
  ]); // Proper dependencies

  // Parse dates from URL strings - memoize to prevent infinite loops
  const currentStartDate = useMemo(() => parseDateString(urlStartDate), [urlStartDate]);
  const currentEndDate = useMemo(() => parseDateString(urlEndDate), [urlEndDate]);

  // Internal temporary state
  const [tempStartDate, setTempStartDate] = useState<DateValue | null>(currentStartDate);
  const [tempEndDate, setTempEndDate] = useState<DateValue | null>(currentEndDate);

  // Sync temp state if URL values change
  useEffect(() => {
    setTempStartDate(currentStartDate);
    setTempEndDate(currentEndDate);
  }, [currentStartDate, currentEndDate]);

  const handleApply = useCallback(() => {
    // Ensure end date is not before start date
    let finalEndDate = tempEndDate;
    if (tempStartDate && tempEndDate && tempEndDate.compare(tempStartDate) < 0) {
      finalEndDate = tempStartDate;
      setTempEndDate(finalEndDate);
    }

    setUrlStartDate(tempStartDate ? formatDateValue(tempStartDate) : null);
    setUrlEndDate(finalEndDate ? formatDateValue(finalEndDate) : null);
  }, [tempStartDate, tempEndDate, setUrlStartDate, setUrlEndDate]);

  const handleClear = useCallback(() => {
    setTempStartDate(null);
    setTempEndDate(null);
    setUrlStartDate(null);
    setUrlEndDate(null);
  }, [setUrlStartDate, setUrlEndDate]);

  const handlePresetSelect = useCallback(
    (preset: DateRangePreset) => {
      const { start, end } = preset.getDates();
      setTempStartDate(start);
      setTempEndDate(end);
      // Immediately apply the preset
      setUrlStartDate(formatDateValue(start));
      setUrlEndDate(formatDateValue(end));
    },
    [setUrlStartDate, setUrlEndDate]
  );

  const isFilterActive = currentStartDate !== null || currentEndDate !== null;

  const renderActiveOptions = () => {
    const start = formatDateValue(currentStartDate);
    const end = formatDateValue(currentEndDate);

    // Check if current date range matches any preset
    if (showPresets && currentStartDate && currentEndDate) {
      const matchingPreset = presets.find((preset) => {
        const { start: presetStart, end: presetEnd } = preset.getDates();
        return formatDateValue(presetStart) === start && formatDateValue(presetEnd) === end;
      });

      if (matchingPreset) {
        return t(matchingPreset.label);
      }
    }

    // Fallback to date range display
    if (start && end) return `${start} - ${end}`;
    if (start) return start;
    if (end) return end;
    return null;
  };

  return (
    <FilterButton
      isFilterActive={isFilterActive}
      onClearFilter={handleClear}
      onInteractOutside={handleDateFilterInteractOutside}
      positioning={positioning}
    >
      <FilterButtonAction>
        <FilterButtonLabel>{t('filters.dateRange.label')}</FilterButtonLabel>
        {isFilterActive && (
          <FilterButtonActiveOptions>{renderActiveOptions()}</FilterButtonActiveOptions>
        )}
      </FilterButtonAction>
      <Portal>
        <Popover.Positioner>
          <FilterButtonMenu onApply={handleApply}>
            <FilterButtonMenuHeader title={t('filters.dateRange.label')} onClearAll={handleClear} />
            <Box p={3}>
              <VStack
                gap={3}
                alignItems="stretch"
                onClick={(e) => e.stopPropagation()} // Prevent closing parent popover
              >
                {/* Preset Buttons */}
                {showPresets && presets.length > 0 && (
                  <VStack gap={2} alignItems="stretch">
                    <HStack gap={2} flexWrap="wrap">
                      {presets.map((preset) => (
                        <Popover.CloseTrigger key={preset.value} asChild>
                          <Button
                            size="xs"
                            variant="subtle"
                            fontWeight="normal"
                            onClick={() => handlePresetSelect(preset)}
                            colorPalette="gray"
                            rounded="full"
                          >
                            {t(preset.label)}
                          </Button>
                        </Popover.CloseTrigger>
                      ))}
                    </HStack>
                    <Separator />
                  </VStack>
                )}

                {/* Custom Date Pickers */}
                <DatePicker
                  label={t('filters.dateRange.startDate')}
                  value={tempStartDate}
                  onChange={setTempStartDate}
                />
                <DatePicker
                  label={t('filters.dateRange.endDate')}
                  value={tempEndDate}
                  onChange={setTempEndDate}
                  minValue={tempStartDate || undefined}
                />
              </VStack>
            </Box>
          </FilterButtonMenu>
        </Popover.Positioner>
      </Portal>
    </FilterButton>
  );
};
